<script setup lang="ts">
import { EmptyState, TraceIdAlert, formatDateTime } from '@yc/ui-kit'
import { computed, onMounted, reactive, ref } from 'vue'

import {
  LEVELS,
  LEVEL_LABELS,
  LEVEL_TAG,
  SOURCE_MODULES,
  SOURCE_MODULE_LABELS,
  STATUS_LABELS,
  STATUS_TAG,
  alertInput,
  canAct,
  createAlert,
  getAlert,
  listAlerts,
  processAlert,
  resolveAlert,
  type Alert,
  type AlertForm,
  type AlertRow,
  type AlertStatus,
} from '../api/alerts'
import { toRFC3339 } from '../api/datetime'
import {
  ACTION_LABELS,
  ACTION_TYPES,
  STATUS_LABELS as RESPONSE_STATUS_LABELS,
  createResponse,
  listResponses,
  responseInput,
  type ResponseActionType,
  type ResponseRow,
} from '../api/responses'

const rows = ref<AlertRow[]>([])
const total = ref(0)
const loading = ref(false)
const error = ref<unknown>(null)
const notice = ref('')

const query = reactive({ keyword: '', status: '', level: '', source_module: '', page: 1, size: 20 })

/** 新建预警 */
const formOpen = ref(false)
const saving = ref(false)
const formError = ref<unknown>(null)
const form = reactive<AlertForm>({
  level: 'warn',
  source_module: 'manual',
  title: '',
  occurred_at: null,
  ref_no: '',
  detail: '',
})

/** 详情：受理 / 解决 / 直接建响应 */
const detailOpen = ref(false)
const detail = ref<Alert | null>(null)
const detailLoading = ref(false)
const detailError = ref<unknown>(null)
/** 受理与解决的失败（非法流转是 409），与加载失败分开显示，都带 trace_id */
const actionError = ref<unknown>(null)

/** 详情内已挂的响应：后端 List 支持按 alert_id 过滤 */
const linked = ref<ResponseRow[]>([])
const linkedLoading = ref(false)

/** 详情内建响应（scr:response:create）：alert_id 由详情固定，表单只填动作 / 负责人 / 截止 */
const linkedForm = reactive({
  action: '' as ResponseActionType | '',
  owner: '',
  due_at: null as string | null,
  detail: '',
})
const linkedSaving = ref(false)
const linkedError = ref<unknown>(null)

/** 处理（受理）弹窗：handled_by 落库，前端必填 */
const processOpen = ref(false)
const processTarget = ref<Alert | null>(null)
const processSaving = ref(false)
const processError = ref<unknown>(null)
const processForm = reactive({ handled_by: '' })

/** 解决：detail 追加到预警 detail 末尾，可留空 */
const resolveOpen = ref(false)
const resolveTarget = ref<Alert | null>(null)
const resolveSaving = ref(false)
const resolveError = ref<unknown>(null)
const resolveForm = reactive({ detail: '' })

const titleError = computed(() => (form.title.trim() ? '' : '请填写预警标题'))

const occurredError = computed(() =>
  toRFC3339(form.occurred_at) ? '' : '请选择发生时间（后端必填）',
)

const formErrorText = computed(() => titleError.value || occurredError.value)

const processErrorText = computed(() => (processForm.handled_by.trim() ? '' : '请填写处理人'))

const linkedActionError = computed(() => (linkedForm.action ? '' : '请选择响应动作'))

const linkedErrorText = computed(() => {
  if (!toRFC3339(linkedForm.due_at)) return '请选择预计完成时间（后端必填）'
  if (!linkedForm.owner.trim()) return '请填写负责人'
  return linkedActionError.value
})

async function load(noticeText = ''): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const res = await listAlerts({
      page: query.page,
      size: query.size,
      keyword: query.keyword,
      status: query.status,
      level: query.level,
      source_module: query.source_module,
    })
    rows.value = res.list
    total.value = res.total
    notice.value = noticeText
  } catch (e) {
    error.value = e
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(() => void load())

function search(): void {
  query.page = 1
  void load()
}

function page(p: number): void {
  query.page = p
  void load()
}

function resetForm(): void {
  Object.assign(form, {
    level: 'warn' as AlertForm['level'],
    source_module: 'manual' as AlertForm['source_module'],
    title: '',
    occurred_at: null,
    ref_no: '',
    detail: '',
  })
}

function openCreate(): void {
  detailOpen.value = false
  resetForm()
  formError.value = null
  formOpen.value = true
}

async function save(): Promise<void> {
  if (formErrorText.value) return
  saving.value = true
  formError.value = null
  try {
    const created = await createAlert(alertInput(form))
    formOpen.value = false
    await load(`预警 ${created.doc_no} 已创建`)
  } catch (e) {
    // 建单失败（时间格式、级别非法等）原样把 trace_id 露出来
    formError.value = e
  } finally {
    saving.value = false
  }
}

async function openDetail(row: AlertRow): Promise<void> {
  detail.value = null
  detailError.value = null
  actionError.value = null
  linkedError.value = null
  resetLinkedForm()
  detailLoading.value = true
  detailOpen.value = true
  try {
    detail.value = await getAlert(row.id)
    void loadLinked()
  } catch (e) {
    detailError.value = e
  } finally {
    detailLoading.value = false
  }
}

async function loadLinked(): Promise<void> {
  if (!detail.value) return
  linkedLoading.value = true
  try {
    const res = await listResponses({ page: 1, size: 200, alert_id: detail.value.id })
    linked.value = res.list
  } catch (e) {
    linkedError.value = e
  } finally {
    linkedLoading.value = false
  }
}

function resetLinkedForm(): void {
  Object.assign(linkedForm, { action: '', owner: '', due_at: null, detail: '' })
}

async function submitLinked(): Promise<void> {
  if (linkedErrorText.value || !detail.value) return
  linkedSaving.value = true
  linkedError.value = null
  try {
    // 关联对象由详情固定成这张预警：只送 alert_id，绝不同时送 demand_id（后端异或校验会 422）
    const input = responseInput({
      link: 'alert',
      alert_id: detail.value.id,
      action: linkedForm.action,
      owner: linkedForm.owner,
      due_at: linkedForm.due_at,
      detail: linkedForm.detail,
    })
    const created = await createResponse(input)
    resetLinkedForm()
    await loadLinked()
    await load(`响应 ${created.doc_no} 已创建`)
  } catch (e) {
    linkedError.value = e
  } finally {
    linkedSaving.value = false
  }
}

function openProcess(row: Alert): void {
  actionError.value = null
  processError.value = null
  Object.assign(processForm, { handled_by: '' })
  processTarget.value = row
  processOpen.value = true
}

async function submitProcess(): Promise<void> {
  if (processErrorText.value || !processTarget.value) return
  processSaving.value = true
  processError.value = null
  try {
    const updated = await processAlert(processTarget.value.id, {
      handled_by: processForm.handled_by.trim(),
    })
    processOpen.value = false
    await afterAction(updated, '已受理')
  } catch (e) {
    processError.value = e
  } finally {
    processSaving.value = false
  }
}

function openResolve(row: Alert): void {
  actionError.value = null
  resolveError.value = null
  Object.assign(resolveForm, { detail: '' })
  resolveTarget.value = row
  resolveOpen.value = true
}

async function submitResolve(): Promise<void> {
  if (!resolveTarget.value) return
  resolveSaving.value = true
  resolveError.value = null
  try {
    const detailText = resolveForm.detail.trim()
    const updated = await resolveAlert(
      resolveTarget.value.id,
      detailText ? { detail: detailText } : {},
    )
    resolveOpen.value = false
    await afterAction(updated, '已解决')
  } catch (e) {
    resolveError.value = e
  } finally {
    resolveSaving.value = false
  }
}

/** 列表行与详情弹窗都要跟着变：详情开着就替换，否则下次打开重新拉 */
async function afterAction(updated: Alert, verb: string): Promise<void> {
  if (detail.value && detail.value.id === updated.id) {
    detail.value = updated
    void loadLinked()
  }
  await load(`预警 ${updated.doc_no} ${verb}`)
}

function linkedStatusLabel(status: string): string {
  return RESPONSE_STATUS_LABELS[status as keyof typeof RESPONSE_STATUS_LABELS] ?? status
}
</script>

<template>
  <div class="yc-alert">
    <header class="yc-alert__bar">
      <el-input
        v-model="query.keyword"
        class="yc-alert__keyword"
        placeholder="单号 / 标题 / 关联单号"
        clearable
        @keyup.enter="search"
        @clear="search"
      />
      <el-select v-model="query.status" class="yc-alert__ref" placeholder="全部状态" clearable @change="search">
        <el-option v-for="(label, value) in STATUS_LABELS" :key="value" :label="label" :value="value" />
      </el-select>
      <el-select v-model="query.level" class="yc-alert__ref" placeholder="全部级别" clearable @change="search">
        <el-option v-for="l in LEVELS" :key="l" :label="LEVEL_LABELS[l]" :value="l" />
      </el-select>
      <el-select
        v-model="query.source_module"
        class="yc-alert__ref"
        placeholder="全部来源"
        clearable
        @change="search"
      >
        <el-option v-for="m in SOURCE_MODULES" :key="m" :label="SOURCE_MODULE_LABELS[m]" :value="m" />
      </el-select>
      <el-button @click="search">查询</el-button>
      <el-button v-perm="'scr:alert:create'" type="primary" @click="openCreate">新建预警</el-button>
      <el-button @click="load()">刷新</el-button>
      <span v-if="notice" class="yc-alert__notice">{{ notice }}</span>
    </header>

    <div v-if="error" class="yc-alert__error">
      <TraceIdAlert :error="error" />
    </div>

    <el-card v-loading="loading" class="yc-alert__card">
      <el-table v-if="rows.length" :data="rows" row-key="id">
        <el-table-column prop="doc_no" label="预警单号" min-width="140" />
        <el-table-column label="级别" width="90">
          <template #default="{ row }">
            <el-tag :type="LEVEL_TAG[row.level as Alert['level']] ?? 'info'" size="small">
              {{ LEVEL_LABELS[row.level as Alert['level']] ?? row.level }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="来源" width="120">
          <template #default="{ row }">
            {{ SOURCE_MODULE_LABELS[row.source_module as Alert['source_module']] ?? row.source_module }}
          </template>
        </el-table-column>
        <el-table-column label="关联单号" min-width="140" show-overflow-tooltip>
          <template #default="{ row }">{{ row.ref_no || '—' }}</template>
        </el-table-column>
        <el-table-column prop="title" label="标题" min-width="180" show-overflow-tooltip />
        <el-table-column label="状态" width="100">
          <template #default="{ row }">
            <el-tag :type="STATUS_TAG[row.status as AlertStatus] ?? 'info'" size="small">
              {{ STATUS_LABELS[row.status as AlertStatus] ?? row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="发生时间" min-width="155">
          <template #default="{ row }">{{ formatDateTime(row.occurred_at) }}</template>
        </el-table-column>
        <el-table-column label="处理人" width="110">
          <template #default="{ row }">{{ row.handled_by || '—' }}</template>
        </el-table-column>
        <el-table-column label="操作" width="180" fixed="right">
          <template #default="{ row }">
            <el-button link type="primary" @click="openDetail(row)">详情</el-button>
            <el-button
              v-if="canAct(row.status, 'process')"
              v-perm="'scr:alert:process'"
              link
              type="primary"
              @click="openProcess(row)"
            >
              受理
            </el-button>
            <el-button
              v-if="canAct(row.status, 'resolve')"
              v-perm="'scr:alert:resolve'"
              link
              type="success"
              @click="openResolve(row)"
            >
              解决
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <EmptyState
        v-else-if="!loading"
        title="还没有预警"
        description="预警可手工登记（来源模块选「手工上报」），也可由 WMS / OPS / DSP / POD / MTM 的异常回写。"
      >
        <el-button v-perm="'scr:alert:create'" type="primary" @click="openCreate">新建预警</el-button>
      </EmptyState>
    </el-card>

    <el-pagination
      v-if="total > query.size"
      class="yc-alert__page"
      layout="total, prev, pager, next"
      :total="total"
      :page-size="query.size"
      :current-page="query.page"
      @current-change="page"
    />

    <!-- 新建预警 -->
    <el-dialog
      v-if="formOpen"
      v-model="formOpen"
      title="新建预警"
      width="560px"
      :close-on-click-modal="false"
    >
      <div v-if="formError" class="yc-alert__error">
        <TraceIdAlert :error="formError" />
      </div>

      <el-form label-position="top" @submit.prevent="save">
        <el-form-item label="标题" :error="titleError">
          <el-input v-model="form.title" maxlength="255" placeholder="如 运单 YD… 到货破损" />
        </el-form-item>

        <el-form-item label="级别">
          <el-radio-group v-model="form.level">
            <el-radio v-for="l in LEVELS" :key="l" :value="l">{{ LEVEL_LABELS[l] }}</el-radio>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="来源模块">
          <el-select v-model="form.source_module" class="yc-alert__full">
            <el-option
              v-for="m in SOURCE_MODULES"
              :key="m"
              :label="SOURCE_MODULE_LABELS[m]"
              :value="m"
            />
          </el-select>
        </el-form-item>

        <el-form-item label="关联单号">
          <el-input
            v-model="form.ref_no"
            maxlength="64"
            placeholder="如 YD0000000012（来源模块的单号，不跨库校验，可留空）"
          />
        </el-form-item>

        <el-form-item label="发生时间" :error="occurredError">
          <el-date-picker
            v-model="form.occurred_at"
            type="datetime"
            value-format="YYYY-MM-DD HH:mm:ss"
            placeholder="异常实际发生的时间，后端必填"
          />
        </el-form-item>

        <el-form-item label="详情">
          <el-input
            v-model="form.detail"
            type="textarea"
            :rows="3"
            maxlength="2000"
            placeholder="可留空。解决时后端会把解决说明追加到这里。"
          />
        </el-form-item>

        <el-button type="primary" native-type="submit" :loading="saving" :disabled="!!formErrorText">
          创建
        </el-button>
        <span v-if="formErrorText" class="yc-alert__hint yc-alert__hint--inline">{{ formErrorText }}</span>
      </el-form>

      <template #footer>
        <el-button @click="formOpen = false">取消</el-button>
      </template>
    </el-dialog>

    <!-- 详情：受理 / 解决 / 直接建响应 -->
    <el-dialog
      v-if="detailOpen"
      v-model="detailOpen"
      :title="detail ? `预警详情 · ${detail.doc_no}` : '预警详情'"
      width="860px"
      :close-on-click-modal="false"
    >
      <div v-if="detailError || actionError || linkedError" class="yc-alert__error">
        <TraceIdAlert :error="detailError ?? actionError ?? linkedError" />
      </div>

      <div v-loading="detailLoading">
        <template v-if="detail">
          <div class="yc-alert__facts">
            <div class="yc-alert__fact">
              <span>状态</span>
              <el-tag :type="STATUS_TAG[detail.status]" size="small">{{ STATUS_LABELS[detail.status] }}</el-tag>
            </div>
            <div class="yc-alert__fact">
              <span>级别</span>
              <el-tag :type="LEVEL_TAG[detail.level]" size="small">{{ LEVEL_LABELS[detail.level] }}</el-tag>
            </div>
            <div class="yc-alert__fact">
              <span>来源模块</span>{{ SOURCE_MODULE_LABELS[detail.source_module] }}
            </div>
            <div class="yc-alert__fact"><span>关联单号</span>{{ detail.ref_no || '—' }}</div>
            <div class="yc-alert__fact"><span>发生时间</span>{{ formatDateTime(detail.occurred_at) }}</div>
            <div class="yc-alert__fact"><span>处理人</span>{{ detail.handled_by || '—' }}</div>
            <div class="yc-alert__fact">
              <span>受理时间</span>{{ formatDateTime(detail.handled_at) }}
            </div>
            <div class="yc-alert__fact">
              <span>解决时间</span>{{ formatDateTime(detail.closed_at) }}
            </div>
            <div class="yc-alert__fact"><span>创建时间</span>{{ formatDateTime(detail.created_at) }}</div>
          </div>

          <h3 class="yc-alert__sect">详情说明</h3>
          <p class="yc-alert__detail">{{ detail.detail || '—' }}</p>

          <h3 class="yc-alert__sect">响应动作（{{ linked.length }}）</h3>
          <el-table v-if="linked.length" v-loading="linkedLoading" :data="linked" row-key="id" size="small">
            <el-table-column prop="doc_no" label="响应单号" min-width="140" />
            <el-table-column label="动作" width="110">
              <template #default="{ row }">
                {{ ACTION_LABELS[row.action as ResponseActionType] ?? row.action }}
              </template>
            </el-table-column>
            <el-table-column label="负责人" min-width="100">
              <template #default="{ row }">{{ row.owner || '—' }}</template>
            </el-table-column>
            <el-table-column label="截止时间" min-width="155">
              <template #default="{ row }">{{ formatDateTime(row.due_at) }}</template>
            </el-table-column>
            <el-table-column label="状态" width="90">
              <template #default="{ row }">
                <el-tag size="small" type="info">{{ linkedStatusLabel(row.status) }}</el-tag>
              </template>
            </el-table-column>
          </el-table>
          <p v-else-if="!linkedLoading" class="yc-alert__hint">
            这张预警还没有响应动作。响应全部完成后后端会自动把它置为已解决。
          </p>

          <el-form
            v-perm="'scr:response:create'"
            label-position="top"
            class="yc-alert__linked"
            @submit.prevent="submitLinked"
          >
            <div class="yc-alert__linkedrow">
              <el-form-item label="动作" :error="linkedActionError">
                <el-select v-model="linkedForm.action" placeholder="选择响应动作">
                  <el-option
                    v-for="a in ACTION_TYPES"
                    :key="a"
                    :label="ACTION_LABELS[a]"
                    :value="a"
                  />
                </el-select>
              </el-form-item>
              <el-form-item label="负责人">
                <el-input v-model="linkedForm.owner" maxlength="64" placeholder="如 张伟" />
              </el-form-item>
              <el-form-item label="预计完成时间">
                <el-date-picker
                  v-model="linkedForm.due_at"
                  type="datetime"
                  value-format="YYYY-MM-DD HH:mm:ss"
                  placeholder="后端必填"
                />
              </el-form-item>
            </div>
            <el-form-item label="说明">
              <el-input
                v-model="linkedForm.detail"
                maxlength="1000"
                placeholder="可留空"
              />
            </el-form-item>
            <el-button
              type="primary"
              native-type="submit"
              :loading="linkedSaving"
              :disabled="!!linkedErrorText"
            >
              为这张预警建响应
            </el-button>
            <span v-if="linkedErrorText" class="yc-alert__hint yc-alert__hint--inline">
              {{ linkedErrorText }}
            </span>
          </el-form>
        </template>
      </div>

      <template #footer>
        <el-button
          v-if="detail && canAct(detail.status, 'process')"
          v-perm="'scr:alert:process'"
          type="primary"
          @click="openProcess(detail)"
        >
          受理
        </el-button>
        <el-button
          v-if="detail && canAct(detail.status, 'resolve')"
          v-perm="'scr:alert:resolve'"
          type="success"
          @click="openResolve(detail)"
        >
          解决
        </el-button>
        <el-button @click="detailOpen = false">关闭</el-button>
      </template>
    </el-dialog>

    <!-- 受理：handled_by 必填 -->
    <el-dialog
      v-if="processOpen"
      v-model="processOpen"
      :title="processTarget ? `受理预警 · ${processTarget.doc_no}` : '受理预警'"
      width="460px"
      :close-on-click-modal="false"
    >
      <div v-if="processError" class="yc-alert__error">
        <TraceIdAlert :error="processError" />
      </div>

      <el-form label-position="top" @submit.prevent="submitProcess">
        <el-form-item label="处理人" :error="processErrorText">
          <el-input v-model="processForm.handled_by" maxlength="64" placeholder="如 张伟" />
        </el-form-item>
        <p class="yc-alert__hint">受理把状态从「待处理」推到「处理中」，之后才能解决。</p>
        <el-button type="primary" native-type="submit" :loading="processSaving" :disabled="!!processErrorText">
          确认受理
        </el-button>
      </el-form>

      <template #footer>
        <el-button @click="processOpen = false">取消</el-button>
      </template>
    </el-dialog>

    <!-- 解决：说明追加到预警详情末尾 -->
    <el-dialog
      v-if="resolveOpen"
      v-model="resolveOpen"
      :title="resolveTarget ? `解决预警 · ${resolveTarget.doc_no}` : '解决预警'"
      width="460px"
      :close-on-click-modal="false"
    >
      <div v-if="resolveError" class="yc-alert__error">
        <TraceIdAlert :error="resolveError" />
      </div>

      <el-form label-position="top" @submit.prevent="submitResolve">
        <el-form-item label="解决说明">
          <el-input
            v-model="resolveForm.detail"
            type="textarea"
            :rows="3"
            maxlength="1000"
            placeholder="可留空。填写后会以「[解决] …」追加到预警详情。"
          />
        </el-form-item>
        <el-button type="primary" native-type="submit" :loading="resolveSaving">确认解决</el-button>
      </el-form>

      <template #footer>
        <el-button @click="resolveOpen = false">取消</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="scss">
.yc-alert {
  &__bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--yc-space-2);
    margin-bottom: var(--yc-space-4);
  }

  &__keyword {
    width: 220px;
  }

  &__ref {
    width: 140px;
  }

  &__full {
    width: 100%;
  }

  &__notice {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-sm);
  }

  &__error {
    margin-bottom: var(--yc-space-3);
    font-size: var(--yc-font-sm);
  }

  &__page {
    margin-top: var(--yc-space-4);
    justify-content: flex-end;
  }

  &__hint {
    color: var(--yc-text-secondary);
    font-size: var(--yc-font-xs);
    line-height: 1.7;

    &--inline {
      margin-left: var(--yc-space-3);
    }
  }

  &__facts {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
    gap: var(--yc-space-2) var(--yc-space-4);
    margin-bottom: var(--yc-space-4);
  }

  &__fact {
    font-size: var(--yc-font);

    span {
      margin-right: var(--yc-space-2);
      color: var(--yc-text-secondary);
      font-size: var(--yc-font-sm);
    }
  }

  &__sect {
    margin: var(--yc-space-4) 0 var(--yc-space-2);
    font-size: var(--yc-font);
    font-weight: 600;
  }

  &__detail {
    margin: 0;
    padding: var(--yc-space-3);
    border-radius: var(--yc-radius, 6px);
    background: var(--yc-bg-subtle, #f7f8fa);
    font-size: var(--yc-font-sm);
    line-height: 1.8;
    white-space: pre-wrap;
  }

  &__linked {
    margin-top: var(--yc-space-4);
    padding-top: var(--yc-space-3);
    border-top: 1px solid var(--yc-border);
  }

  &__linkedrow {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: var(--yc-space-2) var(--yc-space-3);
  }
}
</style>
